import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import type { AuditId } from "@/packages/types";
import { api } from "convex/_generated/api";
import { ArrowRight, CheckCircle, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface ReportActionCardProps {
  auditId: AuditId;
  onNavigateToReport: () => void;
  index?: number;
}

export function ReportActionCard({
  auditId,
  onNavigateToReport,
  index,
}: ReportActionCardProps) {
  const { t } = useTranslation("audits");
  const { data: report } = useWorkspaceQuery(api.report_queries.getByAudit, {
    auditId,
  });

  // Has report
  if (report) {
    return (
      <WidgetCard
        size="auto"
        title={t("overview.report.title")}
        icon={<FileText size={16} />}
        onClick={onNavigateToReport}
        index={index}
        className="flex-1 min-h-0"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} className="text-emerald-400 shrink-0" />
            <span className="text-sm text-secondary">
              v{report.version} &middot; {report.status}
            </span>
          </div>
          <span className="text-xs text-muted mt-1.5">
            {t("overview.report.generated", {
              date: new Date(report.generatedAt).toLocaleDateString(),
            })}
          </span>

          {/* Bottom action zone — mirrors AI card */}
          <div className="mt-auto pt-2.5 border-t border-border/40">
            <button
              type="button"
              className="group flex items-center justify-between w-full text-xs font-medium text-accent hover:text-accent/80 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onNavigateToReport();
              }}
            >
              <span>{t("overview.report.viewReport")}</span>
              <ArrowRight
                size={13}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
        </div>
      </WidgetCard>
    );
  }

  // No report — designed empty state
  return (
    <WidgetCard
      size="auto"
      title={t("overview.report.title")}
      icon={<FileText size={16} />}
      index={index}
      className="flex-1 min-h-0"
    >
      <motion.div
        className="flex flex-col items-center justify-center h-full gap-1.5 text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-border/60 flex items-center justify-center">
          <FileText size={28} className="text-muted" />
        </div>
        <span className="text-sm text-secondary mt-1">
          {t("overview.report.noReport")}
        </span>
        <Button
          variant="accent-soft"
          size="sm"
          className="mt-1"
          onClick={onNavigateToReport}
        >
          {t("overview.report.create")}
        </Button>
      </motion.div>
    </WidgetCard>
  );
}

export function ReportActionCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="auto" index={index} />;
}
